import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, deviceDetect, helpers, UserContext } from "../utils";
import { Button, Card, CardBody, CardTitle, Col, Dropdown, DropdownItem, DropdownMenu, Row, Table } from "reactstrap";
import { ConfirmModal } from ".";
import _ from "lodash";

function ChangePcrDropdownMenu({ pcrId, isActive, attachmentCount }) {
   const navigate = useNavigate();
   const [open, setOpen] = useState(false);

   function downloadForm() {
      api.postDownloadBinary(
         'Report/PrintChangeRequest/' + _.replace(pcrId, 'PCR-', ''), {}
         , constants.MIME_TYPES.pdf
         , `${pcrId}_${dateHelpers.getDateStringForReportName()}.pdf`
      ).catch((error) => {
         api.catchHandler(error)
         toast.error("There was an error downloading the form");
      }).then((res) => {
         if (res?.data === false) {
            toast.error(res.message);
         }
      }).catch(api.catchHandler);
   }

   return (
      <td className="data-cell" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} style={{ position: "relative" }}>
         <span className="shredLink fw-bold noWrap">{pcrId}</span>
         <Dropdown isOpen={open} toggle={() => setOpen(!open)} style={{ position: "absolute", top: "2em", left: "3em" }}>
            <DropdownMenu>
               {isActive ? (
                  <DropdownItem children="Edit Form" onClick={() => navigate(`${constants.ENDPOINTS.processChangeEdit}/${pcrId}`)} />
               ) : (
                  <DropdownItem children="Print Form" onClick={downloadForm} />
               )}
               <DropdownItem className="sort-handle" onClick={() => navigate(`/attachments/6/${pcrId}`)}>
                  Attachments {attachmentCount > -1 ? `(${attachmentCount})` : null}
               </DropdownItem>
            </DropdownMenu>
         </Dropdown>
      </td>);
}

export default function Home() {
   const navigate = useNavigate();
   const userCtx = useContext(UserContext);
   const { currentUser, appVersion, savedDevice } = userCtx;
   const [coaList, setCoaList] = useState({});
   const [pcrList, setPcrList] = useState({});
   const [showScannerPrompt, setShowScannerPrompt] = useState(false);
   // An account with no roles gets this page and nothing else: every API action 403s for it, and the
   // mobile UIs have nothing it could open, so it is kept on the desktop exactly like a customer.
   const noRoles = helpers.hasNoRoles(currentUser);
   const desktopOnly = currentUser?.isAnyCustomerTypeRole || noRoles;

   useEffect(() => {
      // An iPhone/iPod is authoritatively a camera-only mobile device. An older build (or a stray
      // wedge trip) could store it as Desktop or Handheld Scanner, and a full non-nil entry slips
      // past the needsDevice check below, so it never self-corrects. Re-classify it too — but only
      // for non-customers (customers are always desktop, so re-running would be a no-op that loops
      // on the isMobile===false test). The existing classify path below is already customer-aware.
      const iPhoneNeedsMobile = deviceDetect.isNoScannerMobile()
         && !desktopOnly
         && (savedDevice?.hasScanner === true || savedDevice?.isMobile === false);
      const needsDevice = savedDevice === false || _.isNil(savedDevice) ||
         savedDevice === "undefined" || _.isEqual(savedDevice, { isMobile: false }) || iPhoneNeedsMobile;
      if (needsDevice) {
         // Auto-classify by form factor. Confident desktop and Apple phones (no built-in scanner)
         // apply directly; other mobile devices are asked once whether they have a scanner; a
         // genuinely ambiguous form factor falls back to the manual picker.
         const auto = desktopOnly
            ? constants.DEVICE_TYPES.find(d => !d.isMobile && !d.hasScanner) // customers and role-less accounts are always desktop (matches signIn)
            : deviceDetect.classifyDevice();
         const applyDirectly = auto && (!auto.isMobile || deviceDetect.isNoScannerMobile());
         if (applyDirectly)
            deviceDetect.applyDevice(auto, userCtx, navigate);
         else if (auto && auto.isMobile)
            setShowScannerPrompt(true);
         else
            navigate("/mobile-select");
      }
      else if (currentUser && !desktopOnly) {
         load();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser, savedDevice]);

   function load() {
      api.fetch(`Reference/GetTaskList`)
         .then(response => {
            // Optional-chained for the same reason as CartMenu.getTasks: the shared axios error
            // interceptor resolves failures instead of rejecting, so an expired session lands a 401
            // here as an AxiosError with no .data (and .catch below never runs). An unexpected shape
            // means "no tasks", not a crash.
            if (response?.data?.success) {
               setCoaList(response.data.coaTasks);
               setPcrList(response.data.pcrTasks);
            }
            else {
               setCoaList({});
               setPcrList({});
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            setCoaList({});
            setPcrList({});
         })
   }

   return (
      document.title = `Home`,
      <>
      <DesktopContainer screenName="Welcome To Service Thread">
         <br />
         {/*These are the REQUIRED minimum lengths to allow the sidebar
            to scroll in case someone who is on mobile gets stuck 
            on the desktop home screen and their device is too small*/}
         <div style={{ minHeight: 700, minWidth: 600 }}>
            {/* <div> */}
            <Card className="mobileGrayBgAlt">
               <CardTitle className="mobileBlueBgAlt py-2 px-2 text-white fw-bold">
                  Welcome, {currentUser?.fullName}
               </CardTitle>
               <CardBody className="mobileGrayBgAlt">
                  {noRoles ? (
                     <Row className="align-items-center my-1">
                        <Col className="text-black h6 fw-bold" xs={12}>
                           Your account has no roles assigned. Please contact an administrator.
                        </Col>
                     </Row>
                  ) : (
                     <>
                        <Row className="align-items-center my-1">
                           <Col className="text-black h6 fw-bold" xs={12}>
                              Please make a selection from the menu to navigate to your desired feature
                           </Col>
                        </Row>
                        <Row className="align-items-center my-1">
                           <Col className="text-black h6" xs={12}>
                              Navigating with the browser "back" button may not always behave as expected.
                           </Col>
                        </Row>
                     </>
                  )}
                  <br />

                  {pcrList && pcrList.length ? (
                     <div>
                        <Row className="align-items-center my-1">
                           <Col className="text-black mx-2" xs={12}>Process Changes to Action</Col>
                        </Row>
                        <Row className="align-items-center my-1">
                           <Col xs={12}>
                              <Table className="data-table width600px">
                                 <thead className="table-header">
                                    <tr>
                                       <th width="100">PCR-Id</th>
                                       <th width="80">Active</th>
                                       <th width="100">Created</th>
                                       <th>Process Type</th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {_.map(pcrList, (b, index) =>
                                       <tr key={`b${index}-${b.id}`} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                          <ChangePcrDropdownMenu
                                             pcrId={b.displayId}
                                             isActive={b.isActive}
                                             attachmentCount={b.processChangeAttachmentCount}
                                          />
                                          <td>{b.isActive ? 'Yes' : 'No'}</td>
                                          <td>{dateHelpers.toShortDateString(b.createdDate)}</td>
                                          <td>{b.processTypeString}</td>
                                       </tr>)
                                    }
                                 </tbody>
                              </Table>
                           </Col>
                        </Row>
                        <br />
                     </div>
                  ) : null}

                  {coaList && coaList.length ? (
                     <div>
                        <Row className="align-items-center my-1">
                           <Col className="text-black mx-2" xs={12}>CoA's To Print</Col>
                        </Row>
                        <Row className="align-items-center my-1">
                           <Col xs={12}>
                              <Table className="data-table width1200px">
                                 <thead className="table-header">
                                    <tr>
                                       <th width="100">Requested</th>
                                       <th width="100">Order</th>
                                       <th>Item</th>
                                       <th>Customer</th>
                                       <th width="80">Printed</th>
                                       <th width="120"></th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {_.map(coaList, (b, index) =>
                                       <tr key={`b${index}-${b.id}`} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                          <td>{dateHelpers.toShortDateString(b.actionDate)}</td>
                                          <td>{b.orderId}</td>
                                          <td>{b.itemWithDesc}</td>
                                          <td>{b.custWithName}</td>
                                          <td>{b.userId > 0 ? "Yes" : ""}</td>
                                          <td>
                                             <Button
                                                className="button-success btn-sm"
                                                children={"CoA Search"}
                                                onClick={() => navigate(constants.ENDPOINTS.createCoA, {
                                                   state: {
                                                      currentCustomer: {
                                                         Customer: { value: b.customerId, label: b.custWithName },
                                                         isConsignedCustomer: b.isConsigned
                                                      },
                                                      currentOrder: { value: b.orderId, label: b.orderId },
                                                      currentItem: { value: b.item, label: b.itemWithDesc }
                                                   }
                                                })}
                                             />
                                          </td>
                                       </tr>)
                                    }
                                 </tbody>
                              </Table>
                           </Col>
                        </Row>
                        <br />
                     </div>
                  ) : null}

                  <Row className="align-items-center my-1">
                     <Col className="text-blue" xs={12}>
                        Service Thread Insight Application
                     </Col>
                  </Row>
                  <Row className="align-items-center my-1">
                     <Col className="text-blue" xs={12}>
                        &copy; 2022 - {new Date().getFullYear()} : All rights reserved
                     </Col>
                  </Row>
                  <Row className="align-items-center my-1">
                     <Col className="text-blue">Version {appVersion}</Col>
                  </Row>
               </CardBody>
            </Card>
         </div>
      </DesktopContainer>
      <ConfirmModal
         confirmDelete={showScannerPrompt}
         header="Barcode Scanner"
         warningMessage={<>Does this device have a built-in barcode scanner?</>}
         approveConfirmDelete={() => {
            setShowScannerPrompt(false);
            deviceDetect.applyDevice(constants.DEVICE_TYPES.find(d => d.isMobile && d.hasScanner), userCtx, navigate);
         }}
         declineConfirmDelete={() => {
            setShowScannerPrompt(false);
            deviceDetect.applyDevice(constants.DEVICE_TYPES.find(d => d.isMobile && !d.hasScanner), userCtx, navigate);
         }}
      />
      </>
   );
}
